/**
 * Cached Thunk Wrappers for KPI Data
 *
 * SWR cache layer on top of existing async thunks.
 * - Cache hit (fresh): Returns cached data immediately, skips PENDING
 * - Cache hit (stale): Returns cached data + triggers background refresh (REFRESHING)
 * - Cache miss: Normal thunk flow (PENDING → fetch → SUCCESS)
 *
 * See docs/KPI-SWR-CACHE-IMPLEMENTATION.md for full documentation.
 */

import {
  buildCacheKey,
  fillMissingTimestamps,
  formatDate,
  getTimeGranularity,
  getTimeGranularityOverview,
  httpService,
  kpiCache,
} from '@flexo/general';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';

import {
  BackgroundUpdateComplete as _BackgroundUpdateComplete,
  SetFromCache as _SetFromCache,
  StartRefreshing as _StartRefreshing,
} from './kpi.reducer';

// Cast action creators to any to avoid TypeScript inference issues
const SetFromCache = _SetFromCache as any;
const StartRefreshing = _StartRefreshing as any;
const BackgroundUpdateComplete = _BackgroundUpdateComplete as any;

import {
  SetAncillaryKpiDataCommunityAsync,
  SetAncillaryKpiDataMemberAsync,
  SetAncillaryKpiDataSiteAsync,
  // Comparison
  SetComparisonKpiDataCommunityAsync,
  SetEconomicsCustomKpiDataCommunityAsync,
  SetEconomicsCustomKpiDataMemberAsync,
  SetEconomicsCustomKpiDataSiteAsync,
  SetEconomicsKpiDataCommunityAsync,
  SetEconomicsKpiDataMemberAsync,
  SetEconomicsKpiDataSiteAsync,
  SetEconomicsKwickKpiDataCommunityRankingAsync,
  SetEconomicsKwickKpiDataMemberRankingAsync,
  SetEnergyCustomKpiDataCommunityAsync,
  SetEnergyCustomKpiDataMemberAsync,
  SetEnergyCustomKpiDataSiteAsync,
  SetEnergyKpiDataCommunityAsync,
  SetEnergyKpiDataMemberAsync,
  SetEnergyKpiDataSiteAsync,
  // Ranking
  SetEnergyKwickKpiDataCommunityRankingAsync,
  SetEnergyKwickKpiDataMemberRankingAsync,
} from './async-thunks';

const ENV = (import.meta as any).env;
const LOCALE =
  navigator.language.split('-')[0] ||
  ENV?.VITE_APP_LOCALE?.split('-')[0] ||
  'en';
const MIN_TIME_RESOLUTION = ENV?.VITE_APP_ORG === 'kwick' ? '15min' : null;

// ==================== Types ====================

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

interface CachedThunkConfig {
  /** Redux state field to update (e.g., 'EnergySite') */
  field: string;
  /** Status field to update (e.g., 'EnergyKpiDataSiteAsyncStatus') */
  statusField: string;
  /** Original async thunk to dispatch on cache miss */
  originalThunk: any;
  /** Function to build cache keys from params */
  buildKeys: (params: any) => { overviewKey: string; detailKey: string };
  /** Function to fetch fresh data (for background refresh) */
  fetchFresh: (params: any) => Promise<any>;
}

// ==================== Helpers ====================

const okOrNull = (res: any) => (res?.status === 200 ? res.data : null);
const swallowErr = () => null;

function buildOverviewParams(selectedDate: any, selectedTimeSet: any) {
  return {
    dateFrom: formatDate(selectedDate[0]),
    dateTo: formatDate(selectedDate[1]),
    timeGranularity: getTimeGranularityOverview(selectedTimeSet),
    locale: LOCALE,
  };
}

function buildDetailParams(
  selectedDate: any,
  selectedTimeSet: any,
  minRes: string | null = null
) {
  return {
    dateFrom: formatDate(selectedDate[0]),
    dateTo: formatDate(selectedDate[1]),
    timeGranularity: getTimeGranularity(selectedTimeSet, minRes),
    locale: LOCALE,
  };
}

// ==================== Generic Cached Thunk Factory ====================

function createCachedThunk(config: CachedThunkConfig) {
  const { field, statusField, originalThunk, buildKeys, fetchFresh } = config;

  return (params: any) => {
    return async (dispatch: AppDispatch) => {
      const { overviewKey, detailKey } = buildKeys(params);

      // Check cache for both overview and detail
      const overviewCached = kpiCache.getCached(overviewKey);
      const detailCached = kpiCache.getCached(detailKey);

      // ========== CACHE HIT ==========
      if (
        overviewCached.exists &&
        detailCached.exists &&
        !overviewCached.isExpired &&
        !detailCached.isExpired
      ) {
        const cachedData = {
          '1day': overviewCached.data,
          '1hour': detailCached.data,
        };

        console.log(`[CachedThunk] 🎯 CACHE HIT for ${field}`, {
          overviewStale: overviewCached.isStale,
          detailStale: detailCached.isStale,
        });

        // Dispatch cached data immediately (no PENDING → no skeleton)
        dispatch(
          SetFromCache({
            field,
            statusField,
            data: cachedData,
          })
        );

        // If stale, refresh in background
        if (overviewCached.isStale || detailCached.isStale) {
          console.log(
            `[CachedThunk] 🔄 BACKGROUND REFRESH starting for ${field}`
          );
          dispatch(StartRefreshing({ statusField }));

          // Background fetch
          fetchFresh(params)
            .then((freshData) => {
              if (freshData) {
                // Cache the fresh data
                kpiCache.set(overviewKey, freshData['1day']);
                kpiCache.set(detailKey, freshData['1hour']);

                // Update Redux silently
                dispatch(
                  BackgroundUpdateComplete({
                    field,
                    statusField,
                    data: freshData,
                  })
                );
              }
            })
            .catch((error) => {
              console.warn('[CachedThunk] Background refresh failed:', error);
              // Keep cached data, just set status back to SUCCESS
              dispatch(
                BackgroundUpdateComplete({
                  field,
                  statusField,
                  data: cachedData,
                })
              );
            });
        }

        return cachedData;
      }

      // ========== CACHE MISS ==========
      console.log(`[CachedThunk] ❌ CACHE MISS for ${field}`, {
        overviewExists: overviewCached.exists,
        detailExists: detailCached.exists,
        overviewExpired: overviewCached.isExpired,
        detailExpired: detailCached.isExpired,
      });

      // Normal thunk flow: PENDING → skeleton → fetch → SUCCESS/ERROR
      const result = await dispatch(originalThunk(params));

      // Cache ONLY successful responses (never cache errors)
      if (!result.error && result.payload) {
        console.log(`[CachedThunk] 💾 CACHING response for ${field}`);
        kpiCache.set(overviewKey, result.payload['1day']);
        kpiCache.set(detailKey, result.payload['1hour']);
      } else {
        console.log(
          `[CachedThunk] ⚠️ NOT CACHING (error or no payload) for ${field}`
        );
      }

      return result.payload;
    };
  };
}

// ==================== Site Cached Thunks ====================

export const cachedSetEnergyKpiDataSite = createCachedThunk({
  field: 'EnergySite',
  statusField: 'EnergyKpiDataSiteAsyncStatus',
  originalThunk: SetEnergyKpiDataSiteAsync,
  buildKeys: (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `/community/kpi/v2_0/site/energy/${SITE_ID}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(
      selectedDate,
      selectedTimeSet,
      MIN_TIME_RESOLUTION
    );
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/energy/${SITE_ID}`;
    const [overview, detail] = await Promise.all([
      httpService
        .get({
          url: baseUrl,
          params: buildOverviewParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: baseUrl,
          params: buildDetailParams(
            selectedDate,
            selectedTimeSet,
            MIN_TIME_RESOLUTION
          ),
        })
        .then(okOrNull)
        .catch(swallowErr),
    ]);
    // Fill missing timestamps to match originalThunk behavior
    const filledOverview = fillMissingTimestamps(
      overview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );
    const filledDetail = fillMissingTimestamps(
      detail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );
    return { '1day': filledOverview, '1hour': filledDetail };
  },
});

export const cachedSetEconomicsKpiDataSite = createCachedThunk({
  field: 'EconomicsSite',
  statusField: 'EconomicsKpiDataSiteAsyncStatus',
  originalThunk: SetEconomicsKpiDataSiteAsync,
  buildKeys: (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `/community/kpi/v2_0/site/economics/${SITE_ID}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(selectedDate, selectedTimeSet);
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/economics/${SITE_ID}`;
    const [overview, detail] = await Promise.all([
      httpService
        .get({
          url: baseUrl,
          params: buildOverviewParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: baseUrl,
          params: buildDetailParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
    ]);
    // Fill missing timestamps to match originalThunk behavior
    const filledOverview = fillMissingTimestamps(
      overview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );
    const filledDetail = fillMissingTimestamps(
      detail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, null) as any
    );
    return { '1day': filledOverview, '1hour': filledDetail };
  },
});

export const cachedSetAncillaryKpiDataSite = createCachedThunk({
  field: 'AncillarySite',
  statusField: 'AncillaryKpiDataSiteAsyncStatus',
  originalThunk: SetAncillaryKpiDataSiteAsync,
  buildKeys: (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `/community/kpi/v2_0/site/ancillary/${SITE_ID}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(selectedDate, selectedTimeSet);
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/site/ancillary/${SITE_ID}`;
    const [overview, detail] = await Promise.all([
      httpService
        .get({
          url: baseUrl,
          params: buildOverviewParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: baseUrl,
          params: buildDetailParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
    ]);
    return { '1day': overview, '1hour': detail };
  },
});

// ==================== Community Cached Thunks ====================

export const cachedSetEnergyKpiDataCommunity = createCachedThunk({
  field: 'EnergyCommunity',
  statusField: 'EnergyKpiDataCommunityAsyncStatus',
  originalThunk: SetEnergyKpiDataCommunityAsync,
  buildKeys: (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `/community/kpi/v2_0/community/energy/${COMMUNITY_ID}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(
      selectedDate,
      selectedTimeSet,
      MIN_TIME_RESOLUTION
    );
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/energy/${COMMUNITY_ID}`;
    const [overview, detail] = await Promise.all([
      httpService
        .get({
          url: baseUrl,
          params: buildOverviewParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: baseUrl,
          params: buildDetailParams(
            selectedDate,
            selectedTimeSet,
            MIN_TIME_RESOLUTION
          ),
        })
        .then(okOrNull)
        .catch(swallowErr),
    ]);
    // Fill missing timestamps to match originalThunk behavior
    const filledOverview = fillMissingTimestamps(
      overview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );
    const filledDetail = fillMissingTimestamps(
      detail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );
    return { '1day': filledOverview, '1hour': filledDetail };
  },
});

export const cachedSetEconomicsKpiDataCommunity = createCachedThunk({
  field: 'EconomicsCommunity',
  statusField: 'EconomicsKpiDataCommunityAsyncStatus',
  originalThunk: SetEconomicsKpiDataCommunityAsync,
  buildKeys: (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `/community/kpi/v2_0/community/economics/${COMMUNITY_ID}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(selectedDate, selectedTimeSet);
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/economics/${COMMUNITY_ID}`;
    const [overview, detail] = await Promise.all([
      httpService
        .get({
          url: baseUrl,
          params: buildOverviewParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: baseUrl,
          params: buildDetailParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
    ]);
    // Fill missing timestamps to match originalThunk behavior
    const filledOverview = fillMissingTimestamps(
      overview,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularityOverview(selectedTimeSet) as any
    );
    const filledDetail = fillMissingTimestamps(
      detail,
      formatDate(selectedDate[0]),
      formatDate(selectedDate[1]),
      getTimeGranularity(selectedTimeSet, MIN_TIME_RESOLUTION) as any
    );
    return { '1day': filledOverview, '1hour': filledDetail };
  },
});

export const cachedSetAncillaryKpiDataCommunity = createCachedThunk({
  field: 'AncillaryCommunity',
  statusField: 'AncillaryKpiDataCommunityAsyncStatus',
  originalThunk: SetAncillaryKpiDataCommunityAsync,
  buildKeys: (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `/community/kpi/v2_0/community/ancillary/${COMMUNITY_ID}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(selectedDate, selectedTimeSet);
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    const baseUrl = `${ENV.VITE_APP_BASE_URL}/community/kpi/v2_0/community/ancillary/${COMMUNITY_ID}`;
    const [overview, detail] = await Promise.all([
      httpService
        .get({
          url: baseUrl,
          params: buildOverviewParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
      httpService
        .get({
          url: baseUrl,
          params: buildDetailParams(selectedDate, selectedTimeSet),
        })
        .then(okOrNull)
        .catch(swallowErr),
    ]);
    return { '1day': overview, '1hour': detail };
  },
});

// ==================== Member Cached Thunks ====================

export const cachedSetEnergyKpiDataMember = createCachedThunk({
  field: 'EnergyMember',
  statusField: 'EnergyKpiDataMemberAsyncStatus',
  originalThunk: SetEnergyKpiDataMemberAsync,
  buildKeys: (params) => {
    const { sites, selectedDate, selectedTimeSet } = params;
    const siteKey = sites?.length === 1 ? sites[0] : sites?.join(',');
    const baseUrl = `/community/kpi/v2_0/member/energy/${siteKey}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(
      selectedDate,
      selectedTimeSet,
      MIN_TIME_RESOLUTION
    );
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    // Member thunks have complex logic, fallback to original thunk
    return null; // Will trigger normal flow
  },
});

export const cachedSetEconomicsKpiDataMember = createCachedThunk({
  field: 'EconomicsMember',
  statusField: 'EconomicsKpiDataMemberAsyncStatus',
  originalThunk: SetEconomicsKpiDataMemberAsync,
  buildKeys: (params) => {
    const { sites, selectedDate, selectedTimeSet } = params;
    const siteKey = sites?.length === 1 ? sites[0] : sites?.join(',');
    const baseUrl = `/community/kpi/v2_0/member/economics/${siteKey}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(selectedDate, selectedTimeSet);
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    return null; // Will trigger normal flow
  },
});

export const cachedSetAncillaryKpiDataMember = createCachedThunk({
  field: 'AncillaryMember',
  statusField: 'AncillaryKpiDataMemberAsyncStatus',
  originalThunk: SetAncillaryKpiDataMemberAsync,
  buildKeys: (params) => {
    const { sites, selectedDate, selectedTimeSet } = params;
    const siteKey = sites?.length === 1 ? sites[0] : sites?.join(',');
    const baseUrl = `/community/kpi/v2_0/member/ancillary/${siteKey}`;
    const overviewParams = buildOverviewParams(selectedDate, selectedTimeSet);
    const detailParams = buildDetailParams(selectedDate, selectedTimeSet);
    return {
      overviewKey: buildCacheKey(baseUrl, overviewParams),
      detailKey: buildCacheKey(baseUrl, detailParams),
    };
  },
  fetchFresh: async (params) => {
    return null; // Will trigger normal flow
  },
});

// ==================== Custom KPI Cached Thunks ====================
// Custom KPI thunks have different response structure, using simpler wrapper

function createSimpleCachedThunk(
  field: string,
  statusField: string,
  originalThunk: any,
  buildKey: (params: any) => string
) {
  return (params: any) => {
    return async (dispatch: AppDispatch) => {
      const cacheKey = buildKey(params);
      const cached = kpiCache.getCached(cacheKey);

      // Cache hit
      if (cached.exists && !cached.isExpired) {
        console.log(`[CachedThunk] 🎯 CACHE HIT (simple) for ${field}`, {
          isStale: cached.isStale,
        });
        dispatch(SetFromCache({ field, statusField, data: cached.data }));

        if (cached.isStale) {
          console.log(
            `[CachedThunk] 🔄 BACKGROUND REFRESH (simple) for ${field}`
          );
          dispatch(StartRefreshing({ statusField }));
          dispatch(originalThunk(params))
            .then((result: any) => {
              if (!result.error && result.payload) {
                kpiCache.set(cacheKey, result.payload);
                dispatch(
                  BackgroundUpdateComplete({
                    field,
                    statusField,
                    data: result.payload,
                  })
                );
              }
            })
            .catch(() => {
              dispatch(
                BackgroundUpdateComplete({
                  field,
                  statusField,
                  data: cached.data,
                })
              );
            });
        }

        return cached.data;
      }

      // Cache miss
      console.log(`[CachedThunk] ❌ CACHE MISS (simple) for ${field}`);
      const result = await dispatch(originalThunk(params));
      if (!result.error && result.payload) {
        console.log(`[CachedThunk] 💾 CACHING (simple) for ${field}`);
        kpiCache.set(cacheKey, result.payload);
      }
      return result.payload;
    };
  };
}

export const cachedSetEnergyCustomKpiDataCommunity = createSimpleCachedThunk(
  'EnergyCustomKpiCommunity',
  'EnergyCustomKpiDataCommunityAsyncStatus',
  SetEnergyCustomKpiDataCommunityAsync,
  (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    return buildCacheKey(
      `/community/kpi/v2_0/customKpi/energy/community/${COMMUNITY_ID}`,
      {
        ...buildOverviewParams(selectedDate, selectedTimeSet),
        aggregationLevel: 'site',
      }
    );
  }
);

export const cachedSetEconomicsCustomKpiDataCommunity = createSimpleCachedThunk(
  'EconomicsCustomKpiCommunity',
  'EconomicsCustomKpiDataCommunityAsyncStatus',
  SetEconomicsCustomKpiDataCommunityAsync,
  (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
    return buildCacheKey(
      `/community/kpi/v2_0/customKpi/economics/community/${COMMUNITY_ID}`,
      {
        ...buildOverviewParams(selectedDate, selectedTimeSet),
        aggregationLevel: 'site',
      }
    );
  }
);

export const cachedSetEnergyCustomKpiDataSite = createSimpleCachedThunk(
  'EnergyCustomKpiSite',
  'EnergyCustomKpiDataSiteAsyncStatus',
  SetEnergyCustomKpiDataSiteAsync,
  (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    return buildCacheKey(`/community/kpi/v2_0/customKpi/energy/${SITE_ID}`, {
      ...buildOverviewParams(selectedDate, selectedTimeSet),
      aggregationLevel: 'site',
    });
  }
);

export const cachedSetEconomicsCustomKpiDataSite = createSimpleCachedThunk(
  'EconomicsCustomKpiSite',
  'EconomicsCustomKpiDataSiteAsyncStatus',
  SetEconomicsCustomKpiDataSiteAsync,
  (params) => {
    const { SITE_ID, selectedDate, selectedTimeSet } = params;
    return buildCacheKey(`/community/kpi/v2_0/customKpi/economics/${SITE_ID}`, {
      ...buildOverviewParams(selectedDate, selectedTimeSet),
      aggregationLevel: 'site',
    });
  }
);

export const cachedSetEnergyCustomKpiDataMember = createSimpleCachedThunk(
  'EnergyCustomKpiMember',
  'EnergyCustomKpiDataMemberAsyncStatus',
  SetEnergyCustomKpiDataMemberAsync,
  (params) => {
    const { sites, selectedDate, selectedTimeSet } = params;
    const siteKey = sites?.length === 1 ? sites[0] : sites?.join(',');
    return buildCacheKey(
      `/community/kpi/v2_0/customKpi/energy/member/${siteKey}`,
      {
        ...buildOverviewParams(selectedDate, selectedTimeSet),
        aggregationLevel: 'site',
      }
    );
  }
);

export const cachedSetEconomicsCustomKpiDataMember = createSimpleCachedThunk(
  'EconomicsCustomKpiMember',
  'EconomicsCustomKpiDataMemberAsyncStatus',
  SetEconomicsCustomKpiDataMemberAsync,
  (params) => {
    const { sites, selectedDate, selectedTimeSet } = params;
    const siteKey = sites?.length === 1 ? sites[0] : sites?.join(',');
    return buildCacheKey(
      `/community/kpi/v2_0/customKpi/economics/member/${siteKey}`,
      {
        ...buildOverviewParams(selectedDate, selectedTimeSet),
        aggregationLevel: 'site',
      }
    );
  }
);

// ==================== Ranking Cached Thunks (Kwick) ====================

export const cachedSetEnergyKwickKpiDataCommunityRanking =
  createSimpleCachedThunk(
    'communityRanking',
    'EnergyKpiDataCommunityRankingAsyncStatus',
    SetEnergyKwickKpiDataCommunityRankingAsync,
    (params) => {
      const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
      return buildCacheKey(
        `/community/kpi/v2_0/siteCommunity/energy/ranking/${COMMUNITY_ID}`,
        {
          ...buildOverviewParams(selectedDate, selectedTimeSet),
        }
      );
    }
  );

export const cachedSetEnergyKwickKpiDataMemberRanking = createSimpleCachedThunk(
  'memberRanking',
  'EnergyKpiDataMemberRankingAsyncStatus',
  SetEnergyKwickKpiDataMemberRankingAsync,
  (params) => {
    const { MEMBER_ID, selectedDate, selectedTimeSet } = params;
    return buildCacheKey(
      `/community/kpi/v2_0/siteMember/energy/ranking/${MEMBER_ID}`,
      {
        ...buildOverviewParams(selectedDate, selectedTimeSet),
      }
    );
  }
);

export const cachedSetEconomicsKwickKpiDataCommunityRanking =
  createSimpleCachedThunk(
    'communityRanking',
    'EconomicsKpiDataCommunityRankingAsyncStatus',
    SetEconomicsKwickKpiDataCommunityRankingAsync,
    (params) => {
      const { COMMUNITY_ID, selectedDate, selectedTimeSet } = params;
      return buildCacheKey(
        `/community/kpi/v2_0/siteCommunity/economics/ranking/${COMMUNITY_ID}`,
        {
          ...buildOverviewParams(selectedDate, selectedTimeSet),
        }
      );
    }
  );

export const cachedSetEconomicsKwickKpiDataMemberRanking =
  createSimpleCachedThunk(
    'memberRanking',
    'EconomicsKpiDataMemberRankingAsyncStatus',
    SetEconomicsKwickKpiDataMemberRankingAsync,
    (params) => {
      const { MEMBER_ID, selectedDate, selectedTimeSet } = params;
      return buildCacheKey(
        `/community/kpi/v2_0/siteMember/economics/ranking/${MEMBER_ID}`,
        {
          ...buildOverviewParams(selectedDate, selectedTimeSet),
        }
      );
    }
  );

// ==================== Comparison Cached Thunk ====================

export const cachedSetComparisonKpiDataCommunity = createSimpleCachedThunk(
  'ComparisonCommunity',
  'ComparisonKpiDataCommunityAsyncStatus',
  SetComparisonKpiDataCommunityAsync,
  (params) => {
    const { COMMUNITY_ID, selectedDate, selectedTimeSet, siteIds } = params;
    return buildCacheKey(
      `/community/kpi/v2_0/community/comparison/${COMMUNITY_ID}`,
      {
        ...buildOverviewParams(selectedDate, selectedTimeSet),
        siteIds: siteIds?.join(',') || '',
      }
    );
  }
);
